/* eslint-disable no-nested-ternary */
import { IDataRow } from 'src/pages/analytics/simulator/pricing-simulator/types/simulation-view-table.model';
import { extractFieldsFromFormulae } from 'src/pages/analytics/simulator/pricing-simulator/utils/column-utils';
import {
  calculateValueFromFormula,
  calculatePercentageChange,
  formatNumberUsingRegex,
  extractValue
} from 'src/pages/analytics/simulator/pricing-simulator/utils/number-utils';
import { PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';
import {
  ITableData,
  Header,
  TypeOfTableData,
  MODIFIED_ROW_INDICATOR_KEY
} from 'src/pages/analytics/simulator/pricing-simulator/types/generic-table.model';

export function flattenColumnsHeader(pricingSimulationData: ITableData) {
  const flat = (headers: Header[]): Header[] => {
    return headers?.reduce<Header[]>((acc, header) => {
      if (!header) return acc;

      // Add current header to accumulator
      acc.push(header);

      // Recursively process children
      if (Array.isArray(header.children)) {
        acc.push(...flat(header.children));
      }

      return acc;
    }, []) || [];
  };

  return flat(pricingSimulationData.header);
}


export function flattenColumnsData(tableDataRow: any) {
  let flattenData: any = [];
  const flat = (rowData) => {
    for (let i = 0; i < rowData.length; i++) {
      const o = rowData[i];
      if (o.children) {
        const c = flat(o.children);
        if (c.length) {
          flattenData = flattenData.concat(c.children);
        }
      }
      flattenData.push(o.parent.data);
    }
    return flattenData;
  };
  flat(tableDataRow);
  return flattenData;
}

const getData = (data: any) => ({
  ...data.parent.data
});

export function makeDataForTableNew(tableDataRow: any) {
  const makeDataLevel = (metaData): any[] =>
    metaData.slice().map((data: any) => {
      const hasChildren = data.children && data.children.length > 0;

      const sortedChildren = hasChildren
        ? makeDataLevel(data.children).sort((p1, p2) =>
          (p1?.retailer || '').localeCompare(p2?.retailer || '')
        )
        : undefined;

      return {
        ...getData(data),
        subRows: sortedChildren
      };
    });

  return makeDataLevel(tableDataRow);
}

export function makeDataForTable(tableDataRow: any) {
  const makeDataLevel = (metaData): any[] =>
    metaData.slice().map((data: any) => ({
      ...getData(data),
      subRows:
        data.children && data.children.length > 0
          ? makeDataLevel(data.children)
          : undefined
    }));
  return makeDataLevel(tableDataRow);
}

export function makeDataForNonCollapseTable(tableDataRow: any) {
  const makeDataLevel = (metaData): any[] =>
    metaData.map((data: any) => ({
      ...data.parent.data
    }));
  return makeDataLevel(tableDataRow);
}

const getValueByFormula = (data: any, formula: string, parentField: string) => {
  const fields = extractFieldsFromFormulae(formula);
  const selectChangedFormulaVal: number | string = calculateValueFromFormula(
    formula,
    data.parent.data,
    fields
  );
  if (!Number.isNaN(selectChangedFormulaVal)) {
    data.parent.data[parentField] = selectChangedFormulaVal;
  }
  return data;
};

const calculateFormulaVal = (
  flattenHeaders: Header[],
  comparingField: Header,
  currentField: Header,
  data,
  userSubmittedKeys: string[] = []
) => {
  flattenHeaders
    .filter((metadata) => metadata && metadata.parent.formula)
    .filter(
      (headerData) =>
        headerData.parent.parent_field !== comparingField.parent.parent_field &&
        extractFieldsFromFormulae(headerData.parent.formula).includes(
          currentField.parent.parent_field
        ) &&
        data.parent.data[headerData.parent.parent_field] !== 'NAN'
    )
    .forEach((colHeaderData) => {
      const dependentField = colHeaderData.parent.parent_field
      if (userSubmittedKeys.includes(dependentField)) {
        return;
      }
      data = getValueByFormula(
        data,
        colHeaderData.parent.formula,
        colHeaderData.parent.parent_field
      );
      if (colHeaderData.parent.linked_to_field) {
        data = getValueByFormula(
          data,
          colHeaderData.parent.formula,
          colHeaderData.parent.parent_field
        );
      }
    });
  return data;
};

/* Updating the state value as per the formula */
const getUpdatedData = (
  data: any,
  columnMetadata: any,
  tableMetaData: ITableData,
  newVal?: number,
  userSubmittedKeys: string[] = []
) => {
  if (newVal !== undefined) {
    data.parent.data[columnMetadata.parent.parent_field] = newVal;
  }

  /* Step1: Calculation self change formula if it is present. */
  if (columnMetadata.parent.self_change_formula) {
    data = getValueByFormula(
      data,
      columnMetadata.parent.self_change_formula,
      columnMetadata.parent.parent_field
    );
  }

  /* Flattening header data to check if the current column name is present in the other header columns formula */
  const flattenHeaders = flattenColumnsHeader(tableMetaData);
  flattenHeaders
    .filter((metadata) => metadata && metadata.parent.formula)
    .filter(
      (colData) =>
        (extractFieldsFromFormulae(colData.parent.formula).includes(
          columnMetadata.parent.parent_field
        ) ||
          colData.parent.parent_field ===
          columnMetadata.parent.linked_to_field) &&
        data.parent.data[colData.parent.parent_field] !== 'NAN'
    )
    .forEach((colData) => {
      // Before recalculating dependent field, check if field was already part of user's submission
      // Skip if true to preserve user's explicitly entered value
      const dependentField = colData.parent.parent_field;
      if (userSubmittedKeys.includes(dependentField)) {
        return;
      }
      /*  Step2: If the current column name is present in the header column formula, 
          then it's calculating the value of that field using the 
          formula key(${ colData.parent.formula}) */
      data = getValueByFormula(
        data,
        colData.parent.formula,
        colData.parent.parent_field
      );
      /*  Step3: It's checking whether the linked field for the current column is present.
          If yes, then it is calculating the value for the linked field. */
      if (colData.parent.self_change_formula) {
        data = getValueByFormula(
          data,
          colData.parent.self_change_formula,
          colData.parent.parent_field
        );
      }
      /*  Step4: It's checking whether the linked field for the current column is present.
          If yes, then it is calculating the value for the linked field. */
      if (
        columnMetadata.parent.parent_field !== colData.parent.parent_field &&
        colData.parent.parent_field === columnMetadata.parent.linked_to_field
      ) {
        data.parent.data[colData.parent.parent_field] =
          data.parent.data[columnMetadata.parent.parent_field];
        data = getValueByFormula(
          data,
          colData.parent.self_change_formula,
          colData.parent.parent_field
        );
        data = calculateFormulaVal(
          flattenHeaders,
          columnMetadata,
          colData,
          data,
          userSubmittedKeys
        );
      } else if (
        columnMetadata.parent.parent_field !== colData.parent.parent_field &&
        colData.parent.linked_to_field !== ''
      ) {
        data.parent.data[colData.parent.linked_to_field] =
          data.parent.data[colData.parent.parent_field];
        /*  Step5: Check self change formula is present ,
          If yes, then recalculate the value using self change else use as is */
        const dataForLinkedField = flattenHeaders.find(
          (metadata) =>
            metadata &&
            colData.parent.linked_to_field === metadata.parent.parent_field
        );
        if (
          dataForLinkedField &&
          dataForLinkedField.parent.self_change_formula
        ) {
          data = getValueByFormula(
            data,
            dataForLinkedField.parent.self_change_formula,
            dataForLinkedField.parent.parent_field
          );
        }
        data = calculateFormulaVal(
          flattenHeaders,
          columnMetadata,
          dataForLinkedField,
          data,
          userSubmittedKeys
        );
      }
    });

  // Recalculate the edited field one more time after dependent formulas settle.
  // This avoids stale first-pass results when self_change_formula depends on fields
  // that are updated later in the same evaluation cycle.
  if (columnMetadata.parent.self_change_formula) {
    data = getValueByFormula(
      data,
      columnMetadata.parent.self_change_formula,
      columnMetadata.parent.parent_field
    );
  }

  return {
    ...data.parent.data
  };
};

const getUpdatedDataForDropdown = (
  data: any,
  columnMetadata: any,
  newVal: number
) => {
  data.parent.data[columnMetadata.parent.parent_field] = newVal;
  return {
    ...data.parent.data
  };
};

const getUpdatedDataForCheckbox = (
  data: any,
  columnMetadata: any,
  newVal: number
) => {
  data.parent.data[columnMetadata.parent.parent_field] = newVal;
  return {
    ...data.parent.data
  };
};

export function updateDataForTable(
  tableMetaData: ITableData,
  action: PayloadAction<{
    columnMetadata: any;
    newValue: number;
  }>
) {
  const makeUpdatedDataLevel = (metaData): any[] =>
    metaData.map((data: any) => ({
      parent: {
        data: getUpdatedData(
          data,
          action.payload.columnMetadata,
          tableMetaData,
          action.payload.newValue
        )
      },
      children:
        data.children && data.children.length
          ? makeUpdatedDataLevel(data.children)
          : []
    }));
  return makeUpdatedDataLevel(tableMetaData.row);
}

/* 
  Updating the PPG rows corresponding to the brand
*/
export function getUpdatedRowsForPPG(
  data: any,
  updatedRows,
  flattenHeaders: Header[],
  tableMetaData: ITableData
) {
  updatedRows.forEach((element) => {
    if (element.ppg === data.parent.data.ppg) {
      // eslint-disable-next-line no-param-reassign
      data.parent.data = { ...data.parent.data, ...element };
      const userSubmittedKeys = Object.keys(element)
      if (updatedRows.length) {
        /* Finding the keys for formula calculation */
        userSubmittedKeys.forEach((keyName) => {
          const formulaField = flattenHeaders
            .filter((headerData) => headerData && headerData.parent)
            .find(
              (headerData) =>
                headerData.parent.parent_field === keyName &&
                headerData.parent.self_change_formula
            );
          if (formulaField) {
            /* Updating the value as per the formula for the list price */
            // eslint-disable-next-line no-param-reassign
            data.parent.data = getUpdatedData(
              data,
              formulaField,
              tableMetaData,
              undefined,
              userSubmittedKeys
            );
          }
        });
      }
    }
  });
  return data.parent.data;
}

/* 
  Updating the PPG rows corresponding to the brand
*/
export function updateDataForPPGRows(tableMetaData: any, updatedRows: any) {
  const flattenHeaders = flattenColumnsHeader(tableMetaData);
  const makeUpdatedDataLevel = (metaData): any[] =>
    metaData?.map((data: any) => ({
      parent: {
        data: getUpdatedRowsForPPG(
          data,
          updatedRows,
          flattenHeaders,
          tableMetaData
        )
      },
      children:
        data.children && data.children.length
          ? makeUpdatedDataLevel(data.children)
          : []
    }));
  return makeUpdatedDataLevel(tableMetaData.row);
}

export function updateDataForCheckbox(
  tableMetaData: ITableData,
  action: PayloadAction<{
    columnMetadata: any;
    newValue: number;
  }>
) {
  const makeUpdatedDataLevel = (metaData): any[] =>
    metaData.map((data: any) => ({
      parent: {
        data: getUpdatedDataForCheckbox(
          data,
          action.payload.columnMetadata,
          action.payload.newValue
        )
      },
      children:
        data.children && data.children.length
          ? makeUpdatedDataLevel(data.children)
          : []
    }));
  return makeUpdatedDataLevel(tableMetaData.row);
}
export function updateDataForDropdown(
  tableMetaData: ITableData,
  action: PayloadAction<{
    columnMetadata: any;
    newValue: number;
  }>
) {
  const makeUpdatedDataLevel = (metaData): any[] =>
    metaData.map((data: any) => ({
      parent: {
        data: getUpdatedDataForDropdown(
          data,
          action.payload.columnMetadata,
          action.payload.newValue
        )
      },
      children:
        data.children && data.children.length
          ? makeUpdatedDataLevel(data.children)
          : []
    }));
  return makeUpdatedDataLevel(tableMetaData.row);
}

const getUpdatedRow = (data: any, columnMetadata: any, newVal: number) => {
  data.parent.data[columnMetadata.parent.parent_field] = newVal;
  return {
    ...data.parent.data
  };
};

export function getChangedRow(
  tableMetaData: ITableData,
  action: PayloadAction<{
    columnMetadata: any;
    newValue: number;
  }>
) {
  const updatedRow = [];
  const makeUpdatedDataLevel = (metaData) => {
    metaData.forEach((data: any) => {
      const updatedData =
        data.children && data.children.length
          ? makeUpdatedDataLevel(data.children)
          : getUpdatedRow(
            data,
            action.payload.columnMetadata,
            action.payload.newValue
          );
      if (updatedData !== undefined) {
        updatedRow.push(updatedData);
      }
    });
  };
  makeUpdatedDataLevel(tableMetaData.row);
  return updatedRow;
}

export function getInputChangedRow(
  tableMetaData: ITableData,
  action: PayloadAction<{
    columnMetadata: any;
    newValue: number;
  }>
) {
  const updatedRow = [];
  const makeUpdatedDataLevel = (metaData) => {
    metaData.forEach((data: any) => {
      const updatedData =
        data.children && data.children.length
          ? makeUpdatedDataLevel(data.children)
          : getUpdatedRow(
            data,
            action.payload.columnMetadata,
            action.payload.newValue
          );
      if (updatedData !== undefined) {
        if (
          action.payload.columnMetadata.parent.field_format === 'PERCENTAGE'
        ) {
          const percentageChange = calculatePercentageChange(
            action.payload.newValue,
            updatedData
          );
        }
        updatedRow.push(updatedData);
      }
    });
  };
  makeUpdatedDataLevel(tableMetaData.row);
  return updatedRow;
}

/* eslint-disable no-param-reassign */
export function getColumnsData(
  columnNames: string[],
  rowData: any,
  currentRow: any,
  columnMetadata: Header
) {
  columnNames.forEach((element) => {
    currentRow[element] = rowData[element];
  });
  return currentRow;
}

/* eslint-disable no-param-reassign */
export function getColumnsDataForBoolean(
  columnMetadata: Header,
  newValue: any,
  currentRow: any,
  isRowCanExpand: boolean,
  dataRow,
  columnId = 'ppg_id'
) {
  if (!isRowCanExpand) {
    if (columnId !== 'ppg_id') {
      currentRow.subRows = undefined;
      if (
        currentRow[columnId] === dataRow[columnId] &&
        _.isEqual(currentRow, dataRow)
      ) {
        currentRow[columnMetadata.parent.parent_field] = newValue;
      } else if (
        currentRow[columnId] === dataRow[columnId] &&
        currentRow.channel_id &&
        currentRow.channel_id === dataRow.channel_id &&
        currentRow.retailer_id === 'NAN'
      ) {
        currentRow[columnMetadata.parent.parent_field] = 0;
      } else if (
        currentRow[columnId] === dataRow[columnId] &&
        currentRow.channel_id === 'NAN'
      ) {
        currentRow[columnMetadata.parent.parent_field] = 0;
      }
    } else {
      currentRow[columnMetadata.parent.parent_field] = newValue;
    }
  } else if (dataRow.channel_id !== 'NAN' && dataRow.retailer_id === 'NAN') {
    if (
      currentRow[columnId] === dataRow[columnId] &&
      currentRow.channel_id &&
      currentRow.channel_id === dataRow.channel_id &&
      dataRow.channel_id !== 'NAN'
    ) {
      currentRow[columnMetadata.parent.parent_field] = newValue;
    } else if (
      currentRow[columnId] === dataRow[columnId] &&
      currentRow.retailer_id === 'NAN' &&
      currentRow.channel_id === 'NAN'
    ) {
      currentRow[columnMetadata.parent.parent_field] = 0;
    }
  } else if (
    currentRow[columnId] === dataRow[columnId] &&
    (dataRow.channel_id === 'NAN' ||
      dataRow.sku_id === 'NAN' ||
      currentRow.year_id === 'NAN')
  ) {
    currentRow[columnMetadata.parent.parent_field] = newValue;
  }
  return currentRow;
}

/* eslint-disable no-param-reassign */
export function getColumnsDataForDropdown(
  columnMetadata: Header,
  newValue: any,
  currentRow: any,
  isRowCanExpand: boolean,
  dataRow
) {
  if (!isRowCanExpand) {
    if (
      currentRow.ppg_id === dataRow.ppg_id &&
      _.isEqual(currentRow, dataRow)
    ) {
      currentRow[columnMetadata.parent.parent_field] = newValue;
    }
  } else if (currentRow.ppg_id === dataRow.ppg_id) {
    currentRow[columnMetadata.parent.parent_field] = newValue;
  }
  return currentRow;
}
export function updateDataForSelectedBoolean(
  tableMetaData,
  action: PayloadAction<{
    dataRow: IDataRow;
    newValue: number;
    columnMetadata: Header;
    isRowCanExpand: boolean;
  }>,
  columnId = 'ppg_id'
) {
  const makeUpdatedDataLevel = (metaData): any[] =>
    metaData.map((currentData: any) => ({
      parent: {
        data:
          currentData.parent.data[columnId] === action.payload.dataRow[columnId]
            ? getColumnsDataForBoolean(
              action.payload.columnMetadata,
              action.payload.newValue,
              currentData.parent.data,
              action.payload.isRowCanExpand,
              action.payload.dataRow,
              columnId
            )
            : currentData.parent.data
      },
      children:
        currentData.children && currentData.children.length
          ? makeUpdatedDataLevel(currentData.children)
          : []
    }));
  return makeUpdatedDataLevel(tableMetaData.row);
}

export function updateDataForSelectedDropdown(
  tableMetaData,
  action: PayloadAction<{
    dataRow: IDataRow;
    newValue: number;
    columnMetadata: Header;
    isRowCanExpand: boolean;
  }>,
  columnId = 'ppg_id'
) {
  const makeUpdatedDataLevel = (metaData): any[] =>
    metaData.map((currentData: any) => ({
      parent: {
        data:
          currentData.parent.data[columnId] === action.payload.dataRow[columnId]
            ? getColumnsDataForDropdown(
              action.payload.columnMetadata,
              action.payload.newValue,
              currentData.parent.data,
              action.payload.isRowCanExpand,
              action.payload.dataRow
            )
            : currentData.parent.data
      },
      children:
        currentData.children && currentData.children.length
          ? makeUpdatedDataLevel(currentData.children)
          : []
    }));
  return makeUpdatedDataLevel(tableMetaData.row);
}

export function updateDataForSelectedRow(
  tableMetaData: ITableData,
  columnNames: string[],
  action: PayloadAction<{
    columnMetadata: Header;
    dataRow: IDataRow;
  }>
) {
  const makeUpdatedDataLevel = (metaData): any[] =>
    metaData.map((data: any) => ({
      parent: {
        data:
          data.parent.data.ppg_id === action.payload.dataRow.ppg_id
            ? getColumnsData(
              columnNames,
              action.payload.dataRow,
              data.parent.data,
              action.payload.columnMetadata
            )
            : data.parent.data
      },
      children:
        data.children && data.children.length
          ? makeUpdatedDataLevel(data.children)
          : []
    }));
  return makeUpdatedDataLevel(tableMetaData.row);
}

export function updateDataForPercentageRow(
  tableMetaData: ITableData,
  tableRow: any,
  uniqueId: string,
  action: PayloadAction<{
    columnMetadata: Header;
    dataRow: IDataRow;
    newValue: number;
  }>
) {
  const makeUpdatedDataLevel = (metaData): any[] =>
    metaData.map((data: any) => ({
      parent: {
        data:
          (action.payload.dataRow.retailer === 'NAN' ||
            action.payload.dataRow.sku === 'NAN' ||
            action.payload.dataRow.year === 'NAN') &&
            data.parent.data[uniqueId] === action.payload.dataRow[uniqueId]
            ? getUpdatedData(
              data,
              action.payload.columnMetadata,
              tableMetaData,
              action.payload.newValue
            )
            : ((data.parent.data.retailer &&
              data.parent.data.retailer ===
              action.payload.dataRow.retailer) ||
              (data.parent.data.sku &&
                data.parent.data.sku === action.payload.dataRow.sku) ||
              (data.parent.data.year &&
                data.parent.data.year === action.payload.dataRow.year)) &&
              data.parent.data[uniqueId] === action.payload.dataRow[uniqueId]
              ? getUpdatedData(
                data,
                action.payload.columnMetadata,
                tableMetaData,
                action.payload.newValue
              )
              : data
      },
      children:
        data.children && data.children.length
          ? makeUpdatedDataLevel(data.children)
          : []
    }));
  return makeUpdatedDataLevel(tableRow);
}

// function to extract values from formatted cell value
// const extractValue = (value: string) => {
//   // Remove non-numeric characters except for `.` and `-`
//   const cleanedValue = value.replace(/[^0-9.-]+/g, '');

//   // Parse the cleaned value to a float
//   const numericValue = parseFloat(cleanedValue);

//   if (Number.isNaN(numericValue)) return 0;

//   return numericValue;
// };

export const checkConditionAgainstValue = (
  headerMetaData: Header,
  rowData: any,
  flag?: boolean
) =>
  flag
    ? !headerMetaData.children.length &&
    headerMetaData.parent.is_input_enable &&
    headerMetaData.parent.parent_field &&
    rowData[headerMetaData.parent.parent_field] !==
    headerMetaData.parent.default_value
    :
    headerMetaData.parent.is_input_enable &&
    !headerMetaData.children.length &&
    headerMetaData.parent.parent_field &&
    rowData[headerMetaData.parent.parent_field] &&
    !headerMetaData.parent.default_value_field &&
    rowData[headerMetaData.parent.parent_field] !==
    headerMetaData.parent.default_value;

export const checkConditionAgainstValueForOutput = (
  headerMetaData: Header,
  rowData: any
) => {
  if (headerMetaData.parent.parent_field === 'price_threshold_perc_ui' || headerMetaData.parent.parent_field === "price_threshold_perc") {
    return false;
  }
  return (
    !headerMetaData.children.length &&
    headerMetaData.parent.parent_field &&
    extractValue(
      formatNumberUsingRegex(
        headerMetaData,
        rowData[headerMetaData.parent.parent_field]
      )
    ) !==
    extractValue(
      formatNumberUsingRegex(
        headerMetaData,
        headerMetaData.parent.default_value
      )
    )
  );
};


const RoundToTwoDecimals = (value: any) => {
  const num = Number(value);
  return isNaN(num) ? null : Number(num.toFixed(2));
};

export const checkConditionAgainstProperty = (
  headerMetaData: Header,
  rowData: any
) =>
  headerMetaData.parent.is_input_enable &&
  !headerMetaData.children.length &&
  headerMetaData.parent.parent_field &&
  headerMetaData.parent.default_value_field &&
  RoundToTwoDecimals(rowData[headerMetaData.parent.parent_field]) !==
  RoundToTwoDecimals(rowData[headerMetaData.parent.default_value_field]);

const changeFunc = (
  rowData: any,
  headerMetadata: Header[],
  flattenHeaderMetadata: Header[],
  editedPramName?: string,
  defaultKeyName?: string,
  isUserInput?: string
) =>
  headerMetadata.some((headerMetaData: Header) => {
    if (
      (editedPramName && rowData.parent.data[editedPramName]) ||
      (defaultKeyName && rowData.parent.data[defaultKeyName]) ||
      (isUserInput && rowData.parent.data[isUserInput])
    ) {
      return true;
    }
    if (
      headerMetaData.parent.section === TypeOfTableData.OUTPUT &&
      headerMetaData.parent.parent_field !==
      'pricing_retailer_margin_perc_cal_new' &&
      checkConditionAgainstValueForOutput(headerMetaData, rowData.parent.data)
    ) {
      return true;
    }
    if (checkConditionAgainstValue(headerMetaData, rowData.parent.data)) {
      return true;
    }
    if (checkConditionAgainstProperty(headerMetaData, rowData.parent.data)) {
      return true;
    }
    if (headerMetaData.children.length) {
      return changeFunc(
        rowData,
        headerMetaData.children,
        flattenHeaderMetadata
      );
    }
    return false;
  });

const changeFuncWithBaseline = (
  rowData: any,
  headerMetadata: Header[],
  flattenHeaderMetadata: Header[],
  baselineRowMap: Map<string, any>,
  editedPramName?: string,
  defaultKeyName?: string,
  isUserInput?: string,

) =>
  headerMetadata.some((headerMetaData: Header) => {
    const currentVal = rowData.parent.data[headerMetaData.parent.parent_field];
    const fieldName = headerMetaData.parent.parent_field;
    const data = rowData.parent.data;

    // matching : Try SKU - PPG - Full Key
    let baselineRow = null;
    let matchType = 'none';

    // SKU-level match 
    if (data.sku_id && data.sku_id !== 'NAN') {
      baselineRow = baselineRowMap.get(`sku_${data.sku_id}`);
      if (baselineRow) matchType = 'sku';
    }

    //PPG-level match 
    if (!baselineRow && data.ppg_id && data.ppg_id !== 'NAN') {
      baselineRow = baselineRowMap.get(`ppg_${data.ppg_id}`);
      if (baselineRow) matchType = 'ppg';
    }

    if (!baselineRow) {
      const fullKey = `${data.ppg_id}_${data.channel_id || 'NAN'}_${data.retailer_id || 'NAN'}`;
      baselineRow = baselineRowMap.get(fullKey);
      if (baselineRow) matchType = 'full';
    }


    // If no match found at any level, treat as new/changed
    if (!baselineRow) {
      return true; // No baseline equivalent = changed
    }

    const baselineVal = baselineRow[fieldName];

    //  Check 2: Output column values changed from BASELINE
    if (
      headerMetaData.parent.section === TypeOfTableData.OUTPUT &&
      headerMetaData.parent.parent_field !== 'pricing_retailer_margin_perc_cal_new' &&
      headerMetaData.parent.parent_field !== 'price_threshold_perc_ui' &&
      headerMetaData.parent.parent_field !== 'price_threshold_perc'
    ) {
      // Compare formatted values to handle number formatting
      const currentFormatted = formatNumberUsingRegex(
        headerMetaData,
        currentVal
      );
      const baselineFormatted = formatNumberUsingRegex(
        headerMetaData,
        baselineVal
      );

      if (extractValue(currentFormatted) !== extractValue(baselineFormatted)) {
        return true;
      }
    }

    //  Check 3: Input values changed from BASELINE
    if (
      headerMetaData.parent.is_input_enable &&
      !headerMetaData.children?.length &&
      headerMetaData.parent.parent_field &&
      !headerMetaData.parent.default_value_field &&
      currentVal !== baselineVal
    ) {
      return true;
    }

    //  Check 4: Dynamic default value fields changed from BASELINE
    if (
      headerMetaData.parent.is_input_enable &&
      !headerMetaData.children?.length &&
      headerMetaData.parent.parent_field &&
      headerMetaData.parent.default_value_field &&
      RoundToTwoDecimals(currentVal) !== RoundToTwoDecimals(baselineVal)
    ) {
      return true;
    }

    //  Recurse into children with same baseline reference
    if (headerMetaData.children?.length) {
      return changeFuncWithBaseline(
        rowData,
        headerMetaData.children,
        flattenHeaderMetadata,
        baselineRowMap,  // Pass Map
        editedPramName,
        defaultKeyName,
        isUserInput
      );
    }

    return false;
  });


const getFilteredObject = (parentData, filteredHeader, rowData) => {
  let changedRowForParent = {};
  Object.keys(parentData.parent.data).forEach((element) => {
    if (filteredHeader.includes(element)) {
      changedRowForParent = Object.assign(changedRowForParent, {
        [element]: parentData.parent.data[element]
      });
    }
  });
  return changedRowForParent;
};

export function getDataForSimulatedRowsWithBaseline(
  currentTableMetaData: any,
  baselineTableMetaData: any,
  uniqueKeyIds: string[] = [],
  editedPramName = MODIFIED_ROW_INDICATOR_KEY.PRICING_IS_EDITED,
  defaultKeyName = MODIFIED_ROW_INDICATOR_KEY.PRICING_IS_NOT_DEFAULT,
  isUserInput = MODIFIED_ROW_INDICATOR_KEY.PRICING_IS_USER_INPUT
) {
  const flattenHeaderMetadata = flattenColumnsHeader(currentTableMetaData);

  const filteredHeader = flattenHeaderMetadata
    .filter(headerData => headerData?.parent?.is_part_of_payload)
    .map(headerOpt => headerOpt.parent?.parent_field)
    .filter(Boolean);

  // Build flat maps for both current and baseline 
  const currentFlatMap = new Map();
  const baselineFlatMap = new Map();

  // Recursive flatten that handles children with hierarchical keys using index
  const flattenData = (rows, targetMap, parentKey = '', isBaseline = false) => {
    if (!rows) return;
    rows.forEach((row, idx) => {
      const data = row.parent.data;
      let key: string;

      // Determine base key for this row
      if (parentKey) {
        // This is a child row - append index to parent's key
        key = `${parentKey}::index_${data.index ?? idx}`;
      } else {
        // This is a top-level (parent) row - use existing logic
        key = (data.sku_id && data.sku_id !== 'NAN')
          ? `sku_${data.sku_id}`
          : `${data.ppg_id}_${data.channel_id || 'NAN'}_${data.retailer_id || 'NAN'}`;
      }

      targetMap.set(key, data);

      // Recursively process children if they exist
      if (row.children?.length) {
        flattenData(row.children, targetMap, key, isBaseline);
      }
    });
  };

  flattenData(currentTableMetaData.row, currentFlatMap);
  flattenData(baselineTableMetaData.row || baselineTableMetaData, baselineFlatMap);

  const simulatedPayload = { data: [], inputs: [] };

  //Track if baseline has children that current doesn't (deleted children)
  const currentKeys = new Set(currentFlatMap.keys());
  const baselineKeys = new Set(baselineFlatMap.keys());

  // Find keys in baseline but not in current (deleted children/rows)
  const deletedKeys = [...baselineKeys].filter(key => !currentKeys.has(key));

  // Find keys in current but not in baseline (new children/rows)
  const newKeys = [...currentKeys].filter(key => !baselineKeys.has(key));

  // Compare current against baseline - REUSES EXISTING COMPARISON LOGIC
  currentFlatMap.forEach((currentRowData, key) => {
    const baselineRowData = baselineFlatMap.get(key);

    // If no baseline match, treat as new/changed (new row or new child)
    if (!baselineRowData) {
      let changedRow = {};
      filteredHeader.forEach(field => {
        if (currentRowData[field] !== undefined) {
          changedRow[field] = currentRowData[field];
        }
      });
      // Add source identifier for debugging
      changedRow['_key'] = key;
      simulatedPayload.data.push(changedRow);
      return;
    }

    // REUSE: Same comparison logic as before, now works for both parent and child rows
    let hasChange = false;
    const changedRow = {};

    flattenHeaderMetadata.forEach(header => {
      const fieldName = header.parent.parent_field;
      const currentVal = currentRowData[fieldName];
      const baselineVal = baselineRowData[fieldName];

      if (!fieldName || currentVal === undefined) return;

      let isFieldChanged = false;

      // Output columns - exact same logic as before
      if (
        header.parent.section === TypeOfTableData.OUTPUT &&
        fieldName !== 'pricing_retailer_margin_perc_cal_new' &&
        fieldName !== 'price_threshold_perc_ui' &&
        fieldName !== 'price_threshold_perc'
      ) {
        const currentFormatted = formatNumberUsingRegex(header, currentVal);
        const baselineFormatted = formatNumberUsingRegex(header, baselineVal);
        isFieldChanged = extractValue(currentFormatted) !== extractValue(baselineFormatted);
      }
      // Input columns - exact same logic as before
      else if (
        header.parent.is_input_enable &&
        !header.children?.length
      ) {
        if (header.parent.default_value_field) {
          isFieldChanged = RoundToTwoDecimals(currentVal) !== RoundToTwoDecimals(baselineVal);
        } else {
          isFieldChanged = currentVal !== baselineVal;
        }
      }

      if (isFieldChanged) {
        hasChange = true;
        changedRow[fieldName] = currentVal;
      }
    });

    if (hasChange) {
      const fallbackRow = changedRow && Object.keys(changedRow).length > 0
        ? changedRow
        : {
          [key.startsWith('sku_') ? 'sku_id' : key.includes('::index_') ? 'index' : 'ppg_id']:
            currentRowData[key.startsWith('sku_') ? 'sku_id' : 'index'] || '_row',
          _source: 'fallback',
          _key: key,
          timestamp: new Date().toISOString()
        };

      simulatedPayload.inputs.push(fallbackRow);
    }
  });

  //Handle deleted children (present in baseline but not in current)
  deletedKeys.forEach(key => {
    const baselineRowData = baselineFlatMap.get(key);
    let deletedRow = {};
    filteredHeader.forEach(field => {
      if (baselineRowData[field] !== undefined) {
        deletedRow[field] = baselineRowData[field];
      }
    });
    deletedRow['_deleted'] = true;
    deletedRow['_key'] = key;
    simulatedPayload.data.push(deletedRow);
  });

  return simulatedPayload;
}

export function getDataForInnovationBaseline(
  currentTableMetaData: any,
  baselineTableMetaData: any,
  uniqueIdField: string
) {
  const flattenHeaderMetadata = flattenColumnsHeader(currentTableMetaData);

  const filteredHeader = flattenHeaderMetadata
    .filter(headerData => headerData?.parent?.is_part_of_payload)
    .map(headerOpt => headerOpt.parent?.parent_field)
    .filter(Boolean);

  // Build flat maps for both current and baseline using SKU as unique key
  const currentFlatMap = new Map();
  const baselineFlatMap = new Map();

  const flattenData = (rows, targetMap) => {
    if (!rows) return;
    rows.forEach(row => {
      const data = row.parent.data;
      const uniqueId = data[uniqueIdField];

      // Use only the specified unique field (sku) as key
      const key = `${uniqueIdField}_${uniqueId}`;

      // Store with field-level granularity
      targetMap.set(key, data);
    });
  };

  flattenData(currentTableMetaData.row, currentFlatMap);
  flattenData(baselineTableMetaData.row || baselineTableMetaData, baselineFlatMap);

  const simulatedPayload = { data: [], inputs: [] };

  // Detect if SKU sets are different (added, deleted, or replaced)
  const currentKeys = new Set(currentFlatMap.keys());
  const baselineKeys = new Set(baselineFlatMap.keys());

  // Check for any differences in SKU keys
  //const hasDifferentSkus =
    //currentKeys.size !== baselineKeys.size ||
    //[...currentKeys].some(key => !baselineKeys.has(key)) ||
    //[...baselineKeys].some(key => !currentKeys.has(key));
    const hasAddedSkus = [...currentKeys].some(key => !baselineKeys.has(key));

  // Track which SKUs are new (in current but not in baseline)
  const newSkuKeys = [...currentKeys].filter(key => !baselineKeys.has(key));

  // Add new SKUs to payload.data
  newSkuKeys.forEach(key => {
    const currentRowData = currentFlatMap.get(key);
    let changedRow = {};
    filteredHeader.forEach(field => {
      if (currentRowData[field] !== undefined) {
        changedRow[field] = currentRowData[field];
      }
    });
    simulatedPayload.data.push(changedRow);
  });

  // Compare current against baseline for existing SKUs only
  currentFlatMap.forEach((currentRowData, key) => {
    const baselineRowData = baselineFlatMap.get(key);

    // Skip if this is a new SKU (already handled above)
    if (!baselineRowData) {
      return;
    }

    // Check if any field changed
    let hasChange = false;
    const changedRow = {};

    // Check if all three innovation KPIs are zero
    const innovationMac = currentRowData['innovation_mac'];
    const innovationSalesUnits = currentRowData['innovation_sales_units'];
    const innovationNsv = currentRowData['innovation_nsv'];

    const isMacZero = innovationMac === 0 || innovationMac === null || innovationMac === undefined;
    const isSalesUnitsZero = innovationSalesUnits === 0 || innovationSalesUnits === null || innovationSalesUnits === undefined;
    const isNsvZero = innovationNsv === 0 || innovationNsv === null || innovationNsv === undefined;

    const hasAllKpis = 'innovation_mac' in currentRowData &&
      'innovation_sales_units' in currentRowData &&
      'innovation_nsv' in currentRowData;

    if (hasAllKpis && isMacZero && isSalesUnitsZero && isNsvZero) {
      hasChange = true;
      // Add the KPI fields to changedRow to indicate what triggered the change
      changedRow['innovation_mac'] = innovationMac;
      changedRow['innovation_sales_units'] = innovationSalesUnits;
      changedRow['innovation_nsv'] = innovationNsv;
    }

    flattenHeaderMetadata.forEach(header => {
      const fieldName = header.parent.parent_field;
      const currentVal = currentRowData[fieldName];
      const baselineVal = baselineRowData[fieldName];

      // Skip if field not relevant or current value is undefined
      if (!fieldName || currentVal === undefined) return;

      let isFieldChanged = false;

      // Skip specific non-comparable fields
      if (
        fieldName !== 'pricing_retailer_margin_perc_cal_new' &&
        fieldName !== 'price_threshold_perc_ui' &&
        fieldName !== 'price_threshold_perc'
      ) {
        // Check if this is a numeric field that needs formatting
        const isNumericField = header.parent.field_format === 'NUMBER' ||
          header.parent.field_format === 'PERCENTAGE' ||
          header.parent.field_format === 'CURRENCY' ||
          typeof currentVal === 'number';

        if (isNumericField) {
          // Handle numeric fields with formatting
          const currentFormatted = formatNumberUsingRegex(header, currentVal);
          const baselineFormatted = formatNumberUsingRegex(header, baselineVal);

          // Safely extract values, treating null/undefined as empty string or 0
          const currentExtracted = currentFormatted ? extractValue(currentFormatted) : 0;
          const baselineExtracted = baselineFormatted ? extractValue(baselineFormatted) : 0;

          isFieldChanged = currentExtracted !== baselineExtracted;
        } else {
          // Handle string and other non-numeric fields with simple equality
          // Treat null/undefined as empty string for comparison
          const currentStr = currentVal !== null && currentVal !== undefined ? String(currentVal) : '';
          const baselineStr = baselineVal !== null && baselineVal !== undefined ? String(baselineVal) : '';
          isFieldChanged = currentStr !== baselineStr;
        }
      }
      // Input columns (explicitly enabled inputs)
      else if (
        header.parent.is_input_enable &&
        !header.children?.length
      ) {
        if (header.parent.default_value_field) {
          // Handle null/undefined values for decimal comparison
          const currentRounded = currentVal !== null && currentVal !== undefined
            ? RoundToTwoDecimals(currentVal)
            : null;
          const baselineRounded = baselineVal !== null && baselineVal !== undefined
            ? RoundToTwoDecimals(baselineVal)
            : null;
          isFieldChanged = currentRounded !== baselineRounded;
        } else {
          isFieldChanged = currentVal !== baselineVal;
        }
      }

      if (isFieldChanged) {
        hasChange = true;
        changedRow[fieldName] = currentVal;
      }
    });

    // Flag change if SKUs were replaced/added/deleted or fields changed
    if (hasAddedSkus || hasChange) {
      // Add to appropriate array
      // fallback to prevent empty payload
      const fallbackRow = changedRow && Object.keys(changedRow).length > 0
        ? changedRow
        : {
          [uniqueIdField]: currentRowData[uniqueIdField] || '_sku',
          _source: 'fallback',
          timestamp: new Date().toISOString()
        };

      simulatedPayload.inputs.push(fallbackRow);
    }
  });

  return simulatedPayload;
}

export function getDataForSimulatedRows(
  // tableMetaData: ITableData,
  tableMetaData: any,
  uniqueKeyIds: string[] = [],
  editedPramName = MODIFIED_ROW_INDICATOR_KEY.PRICING_IS_EDITED,
  defaultKeyName = MODIFIED_ROW_INDICATOR_KEY.PRICING_IS_NOT_DEFAULT,
  isUserInput = MODIFIED_ROW_INDICATOR_KEY.PRICING_IS_USER_INPUT
) {
  /* Flatten the nested array of header data. */
  const flattenHeaderMetadata = flattenColumnsHeader(tableMetaData);
  // Add null checks when accessing nested properties
  const filteredHeader = flattenHeaderMetadata
    .filter(headerData => headerData?.parent?.is_part_of_payload)
    .map(headerOpt => headerOpt.parent?.parent_field)
    .filter(Boolean); // Remove undefined values

  // Add optional chaining for child field metadata
  const childFieldMetadata = flattenHeaderMetadata.filter(
    headerData => headerData?.parent?.child_field
  );
  const simulatedPayload = {
    data: [],
    inputs: []
  };

  const getSimulatedRows = (tableRowData): any[] =>
    tableRowData.forEach((rowData: any) => {
      /* Function to check weather the input value is changed or not */
      const isInputChanged: boolean = changeFunc(
        rowData,
        tableMetaData.header,
        flattenHeaderMetadata,
        editedPramName,
        defaultKeyName,
        isUserInput
      );
      if (
        isInputChanged ||
        rowData.parent.data[editedPramName] ||
        rowData.parent.data[defaultKeyName] ||
        rowData.parent.data[isUserInput]
      ) {
        let changedRow = {};
        const parentDataVal = [];
        Object.keys(rowData.parent.data).forEach((element) => {
          if (filteredHeader.includes(element)) {
            changedRow = Object.assign(changedRow, {
              [element]: rowData.parent.data[element]
            });
          }
        });
        childFieldMetadata.forEach((field) => {
          if (
            rowData.parent.data[field.parent.child_field] !== 'NAN' &&
            rowData.parent.data[field.parent.parent_field] !== 'NAN'
          ) {
            parentDataVal.push(changedRow);
          }
        });
        if (childFieldMetadata.length === parentDataVal.length) {
          simulatedPayload.data.push(changedRow);
        } else {
          simulatedPayload.inputs.push(changedRow);
        }
        if (
          simulatedPayload.inputs.filter(
            (ele) =>
              uniqueKeyIds.length &&
              ele[uniqueKeyIds[0]] === rowData.parent.data[uniqueKeyIds[0]] &&
              ele[uniqueKeyIds[1]] === 'NAN'
          ).length === 0 &&
          editedPramName === MODIFIED_ROW_INDICATOR_KEY.DELIST_IS_EDITED
        ) {
          const parentData = uniqueKeyIds.length
            ? tableMetaData.row.find(
              (row) =>
                row.parent.data[uniqueKeyIds[0]] ===
                rowData.parent.data[uniqueKeyIds[0]]
            )
            : tableMetaData.row[rowData.parent.data.parent_index];
          const changedRowForParent = getFilteredObject(
            parentData,
            filteredHeader,
            rowData
          );
          simulatedPayload.inputs.push(changedRowForParent);
          if (parentData.children.length) {
            parentData.children.forEach((element) => {
              if (
                element.parent.data.channel_id ===
                rowData.parent.data.channel_id &&
                simulatedPayload.inputs.filter(
                  (ele) => ele.channel_id === rowData.parent.data.channel_id
                ).length === 0
              ) {
                const parentPayloadData = getFilteredObject(
                  element,
                  filteredHeader,
                  rowData
                );
                simulatedPayload.inputs.push(parentPayloadData);
              }
            });
          }
        }
      }
      if (rowData.children && rowData.children.length) {
        return getSimulatedRows(rowData.children);
      }
      return null;
    });
  getSimulatedRows(tableMetaData.row);
  return simulatedPayload;
}

export function getDataForEditedSimulatedRows(
  tableMetaData: ITableData,
  editedPramName = MODIFIED_ROW_INDICATOR_KEY.PRICING_IS_EDITED,
  defaultKeyName = MODIFIED_ROW_INDICATOR_KEY.PRICING_IS_NOT_DEFAULT,
  isUserInput = MODIFIED_ROW_INDICATOR_KEY.PRICING_IS_USER_INPUT
) {
  /* Flatten the nested array of header data. */
  const flattenHeaderMetadata = flattenColumnsHeader(tableMetaData);
  const editedSkuField = [];
  const getSimulatedRows = (tableRowData): any[] =>
    tableRowData.forEach((rowData: any) => {
      /* Function to check weather the input value is changed or not */
      const isInputChanged: boolean = changeFunc(
        rowData,
        tableMetaData.header,
        flattenHeaderMetadata,
        editedPramName,
        defaultKeyName,
        isUserInput
      );
      if (isInputChanged) {
        editedSkuField.push(rowData);
      }
      return null;
    });
  getSimulatedRows(tableMetaData.row);
  return editedSkuField;
}

/**
 * Function to set the boolean value based on codition
 * @param  {defaultValueKeyName}  pricing_is_not_default - true if value is other than default value else false
 * @param  {editKeyName}  pricing_is_edited - true if value is other than default value else false
 *
 */
export function updateParentRows(
  tableRowData: any,
  changedRows: any,
  editKeyName: any,
  selectedRow: any,
  columnMetadata: any,
  defaultValueKeyName: string
) {
  const flattenRow = flattenColumnsData(tableRowData)
    .filter((data) => data !== undefined)
    .find((data) => data.id === selectedRow.id);
  flattenRow[editKeyName] = true;
  if (checkConditionAgainstValue(columnMetadata, flattenRow)) {
    flattenRow[defaultValueKeyName] = true;
  } else {
    flattenRow[defaultValueKeyName] = false;
  }
  if (checkConditionAgainstProperty(columnMetadata, flattenRow)) {
    flattenRow[defaultValueKeyName] = true;
  } else {
    flattenRow[defaultValueKeyName] = false;
  }
  changedRows.forEach((element) => {
    if (tableRowData[element.parent_index]) {
      tableRowData[element.parent_index].parent.data[editKeyName] = true;
      if (
        checkConditionAgainstValue(
          columnMetadata,
          tableRowData[element.parent_index].parent.data
        )
      ) {
        tableRowData[element.parent_index].parent.data[defaultValueKeyName] =
          true;
      } else {
        tableRowData[element.parent_index].parent.data[defaultValueKeyName] =
          false;
      }
      if (
        checkConditionAgainstProperty(
          columnMetadata,
          tableRowData[element.parent_index].parent.data
        )
      ) {
        tableRowData[element.parent_index].parent.data[defaultValueKeyName] =
          true;
      } else {
        tableRowData[element.parent_index].parent.data[defaultValueKeyName] =
          false;
      }
    }
  });
}

export default function updateDataForRenovationPercentageRow(
  tableMetaData: ITableData,
  tableRow: any,
  uniqueId: string,
  action: PayloadAction<{
    columnMetadata: Header;
    dataRow: IDataRow;
    newValue: number;
  }>
) {
  const makeUpdatedDataLevel = (metaData): any[] =>
    metaData.map((data: any) => ({
      parent: {
        data:
          action.payload.dataRow.channel === 'NAN' &&
            action.payload.dataRow.retailer === 'NAN' &&
            data.parent.data[uniqueId] === action.payload.dataRow[uniqueId]
            ? getUpdatedData(
              data,
              action.payload.columnMetadata,
              tableMetaData,
              action.payload.newValue
            )
            : data.parent.data.channel &&
              data.parent.data.channel === action.payload.dataRow.channel &&
              data.parent.data.retailer &&
              data.parent.data.retailer === action.payload.dataRow.retailer &&
              data.parent.data[uniqueId] === action.payload.dataRow[uniqueId]
              ? getUpdatedData(
                data,
                action.payload.columnMetadata,
                tableMetaData,
                action.payload.newValue
              )
              : data.parent.data.channel &&
                data.parent.data.channel === action.payload.dataRow.channel &&
                action.payload.dataRow.retailer === 'NAN' &&
                data.parent.data[uniqueId] === action.payload.dataRow[uniqueId]
                ? getUpdatedData(
                  data,
                  action.payload.columnMetadata,
                  tableMetaData,
                  action.payload.newValue
                )
                : data
      },
      children:
        data.children && data.children.length
          ? makeUpdatedDataLevel(data.children)
          : []
    }));
  return makeUpdatedDataLevel(tableRow);
}
